import {
    isValidElement,
    type ReactNode,
    Children,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IPaginationContentProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePaginationContext
} from "../../context";
import {
    getPaginationItems
} from "../../util";
import PaginationEllipsis from "../ellipsis";
import PaginationPrevious from "../previous";
import PaginationStatus from "../status";
import PaginationItem from "../item";
import PaginationNext from "../next";

const PaginationContent: FC<IPaginationContentProps> = ({
    customTheme: customThemeProp,
    children,
    style
}) => {
    const context = usePaginationContext();

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context.customTheme);

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    const isCompactContent = context.variant === "compact" || context.pageCount === undefined;

    const renderItems = (): Array<ReactNode> => {
        if(children) {
            return Children.toArray(children);
        }

        if(isCompactContent || context.pageCount === undefined) {
            return [
                <PaginationPrevious
                    key="previous"
                />,
                <PaginationStatus
                    key="status"
                />,
                <PaginationNext
                    key="next"
                />
            ];
        }

        return [
            <PaginationPrevious
                key="previous"
            />,
            ...getPaginationItems({
                boundaryCount: context.boundaryCount,
                siblingCount: context.siblingCount,
                pageCount: context.pageCount,
                page: context.page
            }).map((item) => {
                if(item.type === "ellipsis") {
                    return <PaginationEllipsis
                        key={item.key}
                    />;
                }

                return <PaginationItem
                    key={`page-${item.page}`}
                    page={item.page}
                />;
            }),
            <PaginationNext
                key="next"
            />
        ];
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="list"
    >
        {renderItems().map((node, index) => <View
            key={isValidElement(node) && node.key !== null ? node.key : `item-${index}`}
            style={stylesheet.item}
            role="listitem"
        >
            {node}
        </View>)}
    </View>;
};
export default PaginationContent;
